import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { ITag } from "@app/interfaces/ITag";
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { DateService, DateUnit } from "@app/services/date.service";
import { LocationInformationService } from "@app/services/location-information/location-information.service";
import { VehicleService } from "@app/services/vehicle.service";
import { VorRecordStatus } from "@components/resourceManagement/vor/vor.interfaces";
import { IVideoEntry } from "@components/videos/models/IVideoEntry";
import { SortType } from "@enums/SortType";
import { IDriver } from "@interfaces/IDriver";
import { IPlot } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { Colour, IDrivingStyleMetricScore } from "@reports/models/IDrivingStyleMetricScore";
import { IReportRowCell } from "@reports/models/IReportRowCell";
import { GeocodeService } from "@services/geocode.service";
import { ReasonCodeService } from "@services/reasonCode.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { FormatDateTypes, VeriDate } from "@services/veriDate.service";
import { ITemperatureProfile } from "@temperature/_models/temperature/ITemperatureProfile";
import * as moment from "moment";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell, VeriTableRowStatus } from "./veriTable.component";

export class VeriTableService {
    private readonly locationInformationService = new HybridServiceAccessor<LocationInformationService>("locationInformationService", this.$injector);

    static $inject = ["$injector", "$q", "dateService", "veriDate", "unitConversionService", "reasonCodeService", "vehicleService", "geocodeService", "translationService"];

    constructor(
        private readonly $injector: angular.auto.IInjectorService,
        private readonly $q: angular.IQService,
        private readonly dateService: DateService,
        private readonly veriDate: VeriDate,
        private readonly unitConversionService: UnitConversionService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly vehicleService: VehicleService,
        private readonly geocodeService: GeocodeService,
        private readonly translationService: TranslationService) {
    }

    createTableRowsWithCells<T>(data: T[], createTableRowCells: (data: T, row?: IVeriTableRow<T>) => IVeriTableRowCell[], createSearchString: (data: T) => string = () => null): IVeriTableRow<T>[] {
        return this.createTableRows(data, data => this.createTableRow(data, (rowData, row) => createTableRowCells(rowData, row)), createSearchString);
    }

    createTableRow<T>(data: T, createTableRowCells: (data: T, row?: IVeriTableRow<T>) => IVeriTableRowCell[]) {

        var row: IVeriTableRow<T> = { cells: [], data: data, };

        row.cells = createTableRowCells(data, row);

        if (!row.cells) {
            return null;
        }

        return row;
    }

    createTableRows<T>(data: T[], createTableRow: (data: T) => IVeriTableRow<T>, createSearchString: (data: T) => string = () => null): IVeriTableRow<T>[] {
        var tableRows: IVeriTableRow<T>[] = [];

        if (data) {
            data.forEach(d => {
                var row = createTableRow(d);

                if (row) {
                    row.searchString = createSearchString(d);
                    tableRows.push(row);
                }
            });
        }

        return tableRows;
    }

    createDateTimeHeader(title = this.translationService.t("Common.Time"), additionalParams?: IVeriTableHeader): IVeriTableHeader {
        var header: IVeriTableHeader = {
            title: title,
            width: 150,
            sortable: true,
            sortType: SortType.Date
        }

        return Object.assign(header, additionalParams);
    }

    createDateHeader(title = this.translationService.t("Common.Date"), additionalParams?: IVeriTableHeader): IVeriTableHeader {
        var header: IVeriTableHeader = {
            title: title,
            width: 150,
            sortable: true,
            sortType: SortType.Date,
        }

        return Object.assign(header, additionalParams);
    }

    createDateTimeCell(dateString: string | Date | moment.Moment, outputFormat: FormatDateTypes = null, className: string = null, nullFallback = this.translationService.t("Common.Never")): IVeriTableRowCell {
        if (dateString) {
            const date = this.veriDate.createDate(dateString as Date);

            if (date && date.getFullYear() > 1) {
                return {
                    contents: `{{cell.data | veriDate : '${outputFormat}'}}`,
                    data: date,
                    sortData: date,
                    className: className,
                    exportDataMethod: () => this.$q.when(this.veriDate.formatDate(date, outputFormat))
                }
            }
        }

        return this.createNACell(className, nullFallback);
    }

    createDateTimeCellv2(dateString: string | Date | moment.Moment | null, formatter: (d: string, v: VeriDate, f: FormatDateTypes) => string | null = null, outputFormat: FormatDateTypes = null, className: string = null, nullFallback = this.translationService.t("Common.Never")) {
        dateString = dateString || "2200-01-01T00:00:00Z"; // if null or unidefined then it's Current

        const date = this.veriDate.createDate(dateString as Date);

        const contents = formatter ? formatter(dateString?.toString(), this.veriDate, outputFormat) : `{{cell.data | veriDate : '${outputFormat}'}}`;

        return {
            contents,
            data: date,
            sortData: date,
            className: className,
            exportDataMethod: () => this.$q.when(this.veriDate.formatDate(date, outputFormat)),
        };
    }

    createStackedCell(contentsTop: string, contentsBottom: string, sortData?: any): IVeriTableRowCell {

        if (!contentsTop && !contentsBottom) {

            return this.createNACell();
        }

        return {
            contents: ` <div>${contentsTop}</div>
                        <div class="font-size-11 text-muted ellipsis" style="margin-top: -2px;">${contentsBottom}</div>`,
            sortData: sortData,
            exportData: `${contentsTop} ${contentsBottom}`
        }
    }

    createStackedDateTimeCell(dateString: string | Date | moment.Moment): IVeriTableRowCell {
        if (dateString) {
            const moment = this.veriDate.createMoment(dateString);

            if (moment.year() > 1) {
                return {
                    contents: ` <div>${this.veriDate.formatDate(moment, "time")}</div>
                                <div class="font-size-11 text-muted">${this.veriDate.formatDate(moment, "date")}</div>`,
                    sortData: moment
                }
            }
        }

        return this.createNACell();
    }

    createStackedDateTimeCellWithTimeAgo(dateString: string | Date | moment.Moment, timeAgoClass: String = ""): IVeriTableRowCell {
        if (dateString) {
            const moment = this.veriDate.createMoment(dateString);

            if (moment.year() > 1) {
                return {
                    contents: `<div>${this.veriDate.formatDate(dateString)}</div>
                    <div class="${timeAgoClass}">${this.veriDate.formatDate(dateString, 'timeago')}</div>`,
                    data: moment,
                    sortData: moment
                }
            }
        }

        return this.createNACell();
    }

    createNACell(className?: string, contentsMessage?: string): IVeriTableRowCell {
        return {
            contents: contentsMessage || "N/A",
            sortData: null,
            className: `text-muted font-size-12 ${className}`
        }
    }

    //createTimeOrDateCell(dateString: string | Date): IVeriTableRowCell {
    //    const moment = dateString ? this.veriDate.createMoment(dateString) : null;

    //    return {
    //        contents: this.veriDate.formatDate(moment, "timeordate"),
    //        sortData: moment
    //    }
    //}

    createDriverSummaryPanelCell(driver: IDriver, vehicle: IVehicle, time: moment.Moment, showTBC: boolean = false, className: string = null): IVeriTableRowCell {
        let vehicleDriverSortData = "";
        if (vehicle) {
            vehicleDriverSortData += vehicle.ShortReg;
        }
        if (driver) {
            vehicleDriverSortData += driver.Name;
        }

        let exportData = "TBC";
        if (vehicle) {
            exportData += vehicle.ShortReg;
        }
        if (vehicle && driver) {
            exportData += " - ";
        }
        if (driver) {
            exportData += driver.Name;
        }

        return {
            contents: `<driver-summary-panel
                            [driver]="cell.data.driver"
                            [vehicle]="cell.data.vehicle"
                            [compact-view]="true"
                            [show-tacho]="true"
                            [show-tbc]s="${showTBC}"
                            [time]="cell.data.time"
                            class="${className}">
                            </driver-summary-panel>`,
            sortData: vehicleDriverSortData,
            data: { driver: driver, vehicle: vehicle, time: time },
            exportData: exportData
        }
    }

    createTagsCell(tags: ITag[], resource: any): IVeriTableRowCell {
        var searchString = "";
        var exportData = tags.length > 0 ? "" : " ";

        tags.forEach(tag => {
            searchString += tag.Name;
            exportData += `${tag.Name}, `;
        });

        return { contents: `<tags resource="cell.data" max="1"></tags>`, data: resource, sortData: searchString, exportData: exportData }
    }

    createVehicleRegHeader(additionalParams?: IVeriTableHeader): IVeriTableHeader {
        const header: IVeriTableHeader = {
            title: this.translationService.t("Common.Vehicle"),
            width: 100,
            sortable: true
        };

        return Object.assign(header, additionalParams);
    }

    createVehicleRegCell(vehicle: IVehicle): IVeriTableRowCell {
        return {
            contents: `<vehicle-reg [vehicle]="cell.data"></vehicle-reg>`,
            data: vehicle,
            sortData: vehicle ? `${vehicle.ShortReg}_${vehicle.Reg}` : "", // We put in both Short Reg and Reg to make search work more predictably
            exportData: vehicle ? vehicle.ShortReg : "---",
            createSearchCallback: (vehicle: IVehicle) => {
                return this.vehicleService.createRobustRegPlateSearchString(vehicle);
            }
        }
    }

    createDriverCell(driver: IDriver): IVeriTableRowCell {
        if (!driver) {
            return this.createNACell(null, this.translationService.t("DriverSummaryPanel.NoDriver"));
        }

        return {
            contents: driver.DisplayName,
            data: driver,
            exportData: driver.DisplayName,
        }
    }

    createLocationInformationCell(plotId: number, dataBinding: string = "row.data", plot: IPlot = null): IVeriTableRowCell {
        if (plot?.LocationInformation?.LocationDescription && !plot?.LocationInformation?.GeofenceIds?.length) {
            return {
                contents: plot.LocationInformation.LocationDescription,
                data: plot
            }
        }

        return { contents: `<location-information [plot]="${dataBinding}"></location-information>`, exportDataMethod: () => this.exportLocationInformationMethod(plotId) }
    }

    createScoreCell(reportRowCell: IReportRowCell): IVeriTableRowCell {

        const metricScore = reportRowCell?.MetaData as IDrivingStyleMetricScore;

        if (!metricScore) {
            return this.createNACell();
        }

        return {
            data: Math.round(reportRowCell.Data),
            sortData: reportRowCell.Data,
            exportData: Math.round(reportRowCell.Data).toString(),
            contents: `<div class="cell-colour-${Colour[metricScore.Colour].toLowerCase()} driver-score">${reportRowCell.Contents}</div>`,
            className: "layout-row layout-align-start-center"
        };
    }

    private exportLocationInformationMethod(plotId: number): angular.IPromise<string> {
        if (!plotId) {
            return this.$q.resolve("N/A")
        }

        return this.geocodeService.getLocationInformationFromPlotId(plotId).then(locationInformation => {
            if (locationInformation.GeofenceIds.length) {
                return this.$q.when(
                    this.locationInformationService.service.getGeofenceNamesString(locationInformation)
                );
            }

            return locationInformation.LocationDescription;
        }).catch((error) => {
            console.error("Error retrieving composite location: ", error);

            return this.$q.resolve(null);
        });
    }

    createVideoEntryLocationCell(videoEntry: IVideoEntry, dataBinding: string = "row.data") {
        return { contents: `<video-address [video-entry]="${dataBinding}"></video-address>`, exportDataMethod: () => this.exportVideoEntryLocationMethod(videoEntry.Id) }
    }

    private exportVideoEntryLocationMethod(videoEntryId: number): angular.IPromise<string> {
        if (!videoEntryId) {
            return this.$q.resolve("N/A")
        }

        return this.geocodeService.getLocationInformationFromVideo(videoEntryId).then(location => {
            return location.LocationDescription;
        }).catch((error) => {
            console.error("Error retrieving formatted address: ", error);

            return null
        });
    }

    createVehicleOnlineStatusCell(vehicle: IVehicle, latestOfflineStatusTime?: moment.Moment, liveUpdates?: boolean): IVeriTableRowCell {
        const onlineStatus = vehicle.onlineStatus

        let sortData: Date = onlineStatus.lastOnlineTime;

        if (onlineStatus.lastOnlineTime && onlineStatus.online && latestOfflineStatusTime && this.veriDate.date1IsBeforeDate2(onlineStatus.lastOnlineTime, latestOfflineStatusTime.toDate())) {
            onlineStatus.sortingTime = this.dateService.addOrSubtractTime(latestOfflineStatusTime.toDate(), 1, DateUnit.Sec)
            sortData = onlineStatus.sortingTime;
        }

        return {
            contents: '<vehicle-online-status [vehicle]="cell.data.vehicle" [live]="cell.data.liveUpdates"></vehicle-online-status>',
            data: {
                vehicle,
                liveUpdates
            },
            sortData: sortData || this.veriDate.createMoment(new Date(1900, 0, 0)),
            exportData: sortData != null ? this.veriDate.formatDate(sortData) : this.translationService.t(`VehicleOnlineStatus.CameraOffline`),
            summaryLabel: onlineStatus.online
                ? this.translationService.t(`VehicleOnlineStatus.CameraOnline`)
                : this.translationService.t(`VehicleOnlineStatus.CameraOffline`)
        };
    }

    createStatusCell(text: string, color: string = "grey-background", tooltipText: string = "", showIcon: boolean = true): string {
        const iconString = (showIcon)
            ? `<i class="material-icons s12 mr-5 ${color}" tooltip="${tooltipText}">fiber_manual_record</i>`
            : "";

        return `<div class="layout-row layout-align-start-center">
                    ${iconString} ${text}
                </div>`;
    }

    createVehicleStatusCell(vehicle: IVehicle, showIcon: boolean = true): IVeriTableRowCell {
        const labelNoData = this.translationService.t("VeriTableService.NoData");

        var statusToolTip = vehicle.Plot ? this.veriDate.formatDate(vehicle.Plot.Utc) : labelNoData;
        var vehicleStatus: IVeriTableRowCell = {
            summaryLabel: labelNoData,
            contents: this.createStatusCell(labelNoData, "red-color", statusToolTip, showIcon),
            sortData: vehicle.Plot?.Utc
        };

        if (vehicle.vorRecord && vehicle.vorRecord.status == VorRecordStatus.Active) {
            const labelVehicleOffRoad = this.translationService.t("VeriTableService.VehicleOffRoad");

            statusToolTip = labelVehicleOffRoad;
            vehicleStatus.summaryLabel = labelVehicleOffRoad;
            vehicleStatus.contents = this.createStatusCell(`VOR`, "grey-color", statusToolTip, showIcon);
            vehicleStatus.exportData = labelVehicleOffRoad;

            return vehicleStatus;
        }

        if (vehicle.Plot) {
            if (vehicle.Plot.UtcMoment.isBefore(this.veriDate.createMoment(new Date()).add(-2, "d"))) {
                const labelNoRecentData = this.translationService.t("VeriTableService.NoRecentData");

                vehicleStatus.summaryLabel = labelNoRecentData;
                vehicleStatus.contents = this.createStatusCell(labelNoRecentData, "orange-color", statusToolTip, showIcon)
            }
            else {
                if (this.reasonCodeService.isTravelling(vehicle.Plot.Reason)) {
                    const labelDriving = this.translationService.t("VeriTableService.Driving");

                    vehicleStatus.summaryLabel = labelDriving;
                    vehicleStatus.contents = this.createStatusCell(labelDriving, "green-color", statusToolTip, showIcon);
                } else {
                    const labelStationary = this.translationService.t("VeriTableService.Stationary");

                    vehicleStatus.summaryLabel = labelStationary;
                    vehicleStatus.contents = this.createStatusCell(labelStationary, "grey-color", statusToolTip, showIcon);
                }
            }
        }

        vehicleStatus.exportData = vehicleStatus.summaryLabel;

        return vehicleStatus;
    }

    createGpsIgnitionOnlineStatusCell(plot: IPlot): IVeriTableRowCell {

        if (!plot) {
            return {
                contents: "",
                data: null,
                sortData: null,
                exportData: null
            };
        }

        const isIgnitionOn = this.reasonCodeService.isIgnitionOn(plot.Reason);
        let statusText = this.reasonCodeService.createStatusText(plot);

        if (plot?.Dwell) {
            statusText += `<div class="text-muted font-size-11">&nbsp;for ${this.veriDate.minutesToDurationString(plot.Dwell / 60)}</div>`;
        }

        const tooltipText = isIgnitionOn ? this.translationService.t("VeriTableService.IgnitionOn") : this.translationService.t("VeriTableService.IgnitionOff");

        const statusIcon = `<div class="layout-row layout-align-start-center">
                                <i class="material-icons s12 mr-5 ${isIgnitionOn ? 'green-color' : 'red-color'}" tooltip="${tooltipText}">fiber_manual_record</i> ${statusText}
                            </div>`;

        return {
            contents: statusIcon,
            data: plot,
            sortData: plot.UtcMoment || null,
            exportData: this.veriDate.formatDate(plot?.UtcMoment)
        };
    }


    createTemperatureProfileCell(profile: ITemperatureProfile): IVeriTableRowCell {
        if (!profile) {
            return this.createNACell();
        }

        const lowerBoundConvertedValue = this.unitConversionService.convertTemperatureWithSuffix(profile.LowerBound);
        const upperBoundConvertedValue = this.unitConversionService.convertTemperatureWithSuffix(profile.UpperBound);
        const lowerBoundConvertedExportValue = this.unitConversionService.convertTemperature(profile.LowerBound);
        const upperBoundConvertedExportValue = this.unitConversionService.convertTemperature(profile.UpperBound);

        return {
            contents: `<div>${profile.Name} (${lowerBoundConvertedValue} - ${upperBoundConvertedValue})</div>`,
            data: profile,
            sortData: lowerBoundConvertedValue,
            className: `padding-top-0 padding-bottom-0`,
            exportData: `${profile.Name} (${lowerBoundConvertedExportValue} - ${upperBoundConvertedExportValue})`
        };
    }

    createVehicleResourceGroupDisplayCell(data: IVehicle, groups?: IGroupWithIds[]): IVeriTableRowCell {
        return {
            contents: `<resource-group-display [vehicle]="cell.data[0]" [groups]="cell.data[1]"  max="1"></resource-group-display>`,
            data: [data, groups],
            excludeFromExport: true
        };
    }

    createDriverResourceGroupDisplayCell(data: IDriver): IVeriTableRowCell {
        return {
            contents: '<resource-group-display [driver]="cell.data" [max]="1"></resource-group-display>',
            data: data,
            excludeFromExport: true
        };
    }

    createDownloadProgressCell(videoProgress: number): IVeriTableRowCell {
        return {
            contents: `<horizontal-bar-chart value="${videoProgress}" max-value="100" class-name="green-color"></horizontal-bar-chart>`,
            exportData: `${videoProgress}%`
        };
    }

    createConvertedValueCell(value: number, conversionType: UnitConversionType, precision = 2): IVeriTableRowCell {
        const convertedValue = this.unitConversionService.convert(value, conversionType, precision);

        return {
            contents: `${convertedValue} ${this.unitConversionService.getConversionLabel(conversionType)}`,
            data: value,
            exportData: convertedValue.toString()
        }
    }

    getRowClassNameFromRowStatus(rowStatus: VeriTableRowStatus): string {
        switch (rowStatus) {
            case VeriTableRowStatus.OkChecked:
                return "pass";

            case VeriTableRowStatus.Warning:
                return "warning";

            case VeriTableRowStatus.Error:
                return "error";

            default:
                return "ok";
        }
    }

}
